import { useTranslation } from 'react-i18next'
import { ClipboardList, ListChecks } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { SetupFieldsProps } from '@/modules/types'
import type { SurveySpaceMode } from '@/types/surveys'

/**
 * The surveys space's only question (F-103): a list of surveys, or a space
 * that is one survey. "Several" comes first and is the default, as in Courses.
 */
export const SurveysSpaceFields = ({ config, onChange, errors, disabled = false }: SetupFieldsProps) => {
  const { t } = useTranslation('surveys')
  const mode: SurveySpaceMode = config?.mode === 'single' ? 'single' : 'multi'
  const options = [
    { value: 'multi' as const, icon: ListChecks, title: t('spaceMode.multi'), description: t('spaceMode.multiHint') },
    { value: 'single' as const, icon: ClipboardList, title: t('spaceMode.single'), description: t('spaceMode.singleHint') }
  ]
  return (
    <div className="space-y-2" data-testid="surveys-mode">
      <span className="text-sm font-medium text-gray-700">{t('spaceMode.label')}</span>
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map(({ value, icon: Icon, title, description }) => {
          const selected = mode === value
          return (
            <button key={value} type="button" aria-pressed={selected} data-value={value} disabled={disabled}
              onClick={() => onChange({ ...config, mode: value })}
              className={cn(
                'flex items-start gap-3 rounded-lg border-2 p-3 text-left transition-all',
                'focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1',
                selected ? 'border-primary bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300',
                disabled && 'cursor-not-allowed opacity-50'
              )}>
              <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', selected ? 'text-primary' : 'text-gray-400')} />
              <span className="min-w-0">
                <span className={cn('block text-sm font-medium', selected ? 'text-primary-700' : 'text-gray-900')}>{title}</span>
                <span className="mt-0.5 block text-xs leading-snug text-gray-500">{description}</span>
              </span>
            </button>
          )
        })}
      </div>
      {mode === 'single' && <p className="text-xs text-gray-500">{t('spaceMode.singleNote')}</p>}
      {errors?.mode && <p className="text-xs text-red-600">{errors.mode}</p>}
    </div>
  )
}
